import { ReactNode, useEffect, useRef, useState } from "react";
import { Box, Grid, Modal } from "@mui/material";
import { X } from "@phosphor-icons/react";
import Swal from "sweetalert2";
import {
  AppliedItem,
  MainTitle,
  RaffleContent,
  SmallModalStyle,
} from "../../../styles/theme";
import {
  CustomButtonDisabled,
  CustomButtonGray,
  CustomButtonGreen,
} from "../../../styles/button";
import { CustomText } from "../../../styles/CustomText";
import {
  GRAYPALECONTENTS,
  GREENCONTENTS,
  REDTITLE,
} from "../../../styles/color";
import {
  VehiclePhotoInfo,
  VehiclePhotoUpload,
} from "../../../system/types/Vehicle";
import { useSasToken } from "../../../hooks/useSasToken";
import { useLoadingDispatch } from "../../../system/context/LoadingContext";
import AlertModal from "../../Common/AlertModal";
import VehiclePhotoUploader from "./VehiclePhotoUploader";
import {
  ANGLE_LABEL,
  VehiclePhotoDraft,
  isPhotoSetComplete,
  missingAngles,
  revokeDrafts,
  uploadInterruptedMessage,
  uploadVehiclePhotos,
} from "./vehiclePhoto";

// 사진 칸이 들어가면 기본 작은 모달보다 길어진다. 화면을 넘지 않게 높이를 잡는다.
export const DrivingModalStyle = {
  ...SmallModalStyle,
  maxHeight: "92vh",
};

interface VehicleDrivingFormProps {
  isStart: boolean;
  /** true 면 사진 단계를 먼저 거친다 (업무용 차량 + 본인 처리). 임원용/관리자 대행은 false */
  photoRequired: boolean;
  reservationId: number;
  /** 부모가 쓰던 주행거리 입력 UI 를 그대로 넣는다 */
  mileageInput: ReactNode;
  hint: string;
  submitLabel?: string;
  /** 업로드가 끝난 사진 목록으로 서버 저장. 실패 알림과 닫기는 부모가 처리한다 */
  onSubmit: (photos: VehiclePhotoUpload[]) => Promise<unknown>;
  onClose: () => void;
  /** 운행 종료 때 참고용으로 보여줄 출발 사진 */
  referencePhotos?: VehiclePhotoInfo[];
  /** 고른 사진 장수가 바뀔 때마다 알린다. 닫기 전 확인에 쓴다 */
  onPhotoCountChange?: (count: number) => void;
}

type VehicleDrivingModalProps = Omit<
  VehicleDrivingFormProps,
  "onPhotoCountChange"
> & { open: boolean };

/**
 * 운행 시작/종료 모달. 사진을 찍은 뒤 바깥을 잘못 누르거나 X 를 눌러 창이 닫히면
 * 사진을 다시 찍어야 하므로, 사진이 있으면 닫기 전에 한 번 묻는다.
 * 저장에 성공했을 때 부모가 직접 닫는 경로는 묻지 않는다.
 */
export function VehicleDrivingModal({ open, onClose, ...formProps }: VehicleDrivingModalProps) {
  const [photoCount, setPhotoCount] = useState(0);
  useEffect(() => {
    if (!open) setPhotoCount(0);
  }, [open]);

  const closeWithConfirm = async () => {
    if (photoCount === 0) {
      onClose();
      return;
    }
    const result = await Swal.fire({
      title: "찍은 사진이 사라집니다",
      html: `창을 닫으면 찍은 사진 ${photoCount}장을 다시 찍어야 합니다.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "닫기",
      cancelButtonText: "계속하기",
      confirmButtonColor: REDTITLE,
      cancelButtonColor: GREENCONTENTS,
      reverseButtons: true,
    });
    if (result.isConfirmed) onClose();
  };

  return (
    <Modal
      open={open}
      onClose={closeWithConfirm}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={DrivingModalStyle}>
        <VehicleDrivingForm
          {...formProps}
          onClose={closeWithConfirm}
          onPhotoCountChange={setPhotoCount}
        />
      </Box>
    </Modal>
  );
}

/**
 * 운행 시작/종료 모달 내용. 사진 단계 → 주행거리 단계 순서.
 * 마이페이지(MyCarButton)와 차량 대시보드(VehicleDrivingBox)가 VehicleDrivingModal 로 같이 쓴다.
 */
function VehicleDrivingForm({
  isStart,
  photoRequired,
  reservationId,
  mileageInput,
  hint,
  submitLabel = "저장",
  onSubmit,
  onClose,
  referencePhotos,
  onPhotoCountChange,
}: VehicleDrivingFormProps) {
  const phase = isStart ? "START" : "END";
  const title = isStart ? "운행 시작" : "운행 종료";
  const [step, setStep] = useState<"photo" | "mileage">(
    photoRequired ? "photo" : "mileage"
  );
  const [drafts, setDrafts] = useState<VehiclePhotoDraft[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { refreshSasToken } = useSasToken();
  const Loading = useLoadingDispatch();

  // 모달이 닫히면 미리보기 URL 을 해제한다.
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  // 저장에 성공하면 부모가 모달을 닫으므로, 그 뒤에는 상태를 건드리지 않는다.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
      revokeDrafts(draftsRef.current);
    },
    []
  );

  const photosReady = isPhotoSetComplete(drafts);

  useEffect(() => {
    onPhotoCountChange?.(drafts.length);
  }, [drafts.length, onPhotoCountChange]);

  // 한 장 올라갈 때마다 기억해 둔다. 중간에 끊겨도 다시 저장할 때 올라간 사진은 건너뛴다.
  const rememberUploaded = (uploaded: VehiclePhotoDraft) => {
    if (!mountedRef.current) return;
    setDrafts((current) =>
      current.map((draft) => (draft.key === uploaded.key ? uploaded : draft))
    );
  };

  const goToMileage = () => {
    if (!photosReady) {
      AlertModal(
        "check",
        "차량 사진(앞/뒤/좌/우)을 모두 등록해주세요.<br>누락: " +
          missingAngles(drafts).map((angle) => ANGLE_LABEL[angle]).join(", ")
      );
      return;
    }
    setStep("mileage");
  };

  const submit = async () => {
    if (submitting) return;
    if (photoRequired && !photosReady) {
      setStep("photo");
      return;
    }
    setSubmitting(true);
    let uploadedCount = drafts.filter((draft) => draft.uploaded).length;
    try {
      let photos: VehiclePhotoUpload[] = [];
      if (photoRequired) {
        Loading({ type: "LOADING_MESSAGE", message: "파일 업로드 중입니다." });
        const uploaded = await uploadVehiclePhotos({
          drafts,
          reservationId,
          phase,
          getSasUrl: refreshSasToken,
          onProgress: (done, total) =>
            Loading({
              type: "LOADING_MESSAGE",
              message: `파일 업로드 중입니다. (${done}/${total})`,
            }),
          onUploaded: (draft) => {
            uploadedCount += 1;
            rememberUploaded(draft);
          },
        });
        photos = uploaded.flatMap((draft) =>
          draft.uploaded ? [draft.uploaded] : []
        );
        Loading({ type: "COMPLETE" });
      }
      // 서버 저장 실패(신호 끊김 포함)는 부모가 알린다. 사진은 이미 올라가 있어 다시 누르면 저장만 한다.
      await onSubmit(photos);
    } catch (err) {
      console.error(err);
      AlertModal("msg", uploadInterruptedMessage(uploadedCount));
    } finally {
      Loading({ type: "COMPLETE" });
      if (mountedRef.current) setSubmitting(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <RaffleContent>
          <MainTitle>
            {title}
            {photoRequired && (
              <StepText>{step === "photo" ? " 1/2 사진" : " 2/2 주행거리"}</StepText>
            )}
          </MainTitle>
          <AppliedItem onClick={onClose}>
            <X size={32} />
          </AppliedItem>
        </RaffleContent>

        {step === "photo" ? (
          <>
            <VehiclePhotoUploader
              phase={phase}
              drafts={drafts}
              onChange={setDrafts}
              referencePhotos={isStart ? undefined : referencePhotos}
            />
            <Grid container justifyContent="right" mt="16px">
              {photosReady ? (
                <CustomButtonGreen type="button" onClick={goToMileage}>
                  다음
                </CustomButtonGreen>
              ) : (
                <CustomButtonDisabled type="button" onClick={goToMileage}>
                  사진 4장 필요
                </CustomButtonDisabled>
              )}
            </Grid>
          </>
        ) : (
          <>
            <CustomText type="subtitle" bold400 color={GRAYPALECONTENTS}>
              {hint}
            </CustomText>
            {mileageInput}
            <Grid container justifyContent="right">
              {photoRequired && (
                <CustomButtonGray type="button" onClick={() => setStep("photo")}>
                  이전
                </CustomButtonGray>
              )}
              {submitting ? (
                <CustomButtonDisabled type="button">저장 중</CustomButtonDisabled>
              ) : (
                <CustomButtonGreen type="button" onClick={submit}>
                  {submitLabel}
                </CustomButtonGreen>
              )}
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  );
}

export default VehicleDrivingForm;

function StepText({ children }: { children: ReactNode }) {
  return (
    <span style={{ fontSize: "0.85rem", color: GRAYPALECONTENTS, fontWeight: 400 }}>
      {children}
    </span>
  );
}
